<!--
  AdminSettings.svelte — General platform configuration page.

  Allows admins to update public-facing content (website, terms of service),
  custom sidebar links, email templates, storage, OAuth, and system settings.

  Loads the singleton Settings row on mount, provides an inline form,
  and saves changes via the AppNetwork `updateSettings()` function.
-->
<script lang="ts">
	import { Button, Input, Label, Toggle, Heading, Spinner, Helper, Textarea } from 'flowbite-svelte';
	import AbstractCard from '../../components/AbstractCard.svelte';
	import AbstractDropdown from '../../components/AbstractDropdown.svelte';
	import { onMount } from 'svelte';
	import {
		getSettings,
		updateSettings,
		listEmailTemplates,
		uploadSidebarLinkIcon,
		deleteSidebarLinkIcon
	} from '../../AppNetwork';
	import type { Settings } from '../../AppTypes';
	import { toastSuccess, toastError } from '../../toast';
	import { ConfirmButton } from '../../components';
	import site from '../../site';
	import { t, t2 } from '../../i18n';

	/** Current settings loaded from server. */
	let settings: Settings | null = null;
	/** Whether the initial load is in progress. */
	let loading = $state(true);
	/** Whether a save operation is in progress. */
	let saving = $state(false);
	/** Error message from the last operation. */
	let error = $state('');

	// ── Form fields ──────────────────────────────────────────────

	/** Public website URL shown on login page and emails. */
	let website = $state('');
	/** Display name for the website link (e.g. "Acme Corp"). */
	let websiteName = $state('');
	/** Link to the terms of service page, shown on login/register pages. */
	let termsOfServiceURL = $state('');
	/** Heading text shown on the left branding panel of auth pages. */
	let loginHeading = $state('');
	/** Subheading text shown below the heading on the auth branding panel. */
	let loginSubheading = $state('');
	/** Contact email for account migration / data transfer enquiries. */
	let migrationContactEmail = $state('');
	/** Master active switch — when false, rejects new registrations. */
	let isActive = $state(true);
	/** Custom sidebar links (label + URL + icon image + sort order). */
	let sidebarLinks: { label: string; url: string; iconImageName: string | null; order: number }[] = $state([]);
	/** Tracks which sidebar link index is currently uploading an icon. */
	let uploadingLinkIcon: number | null = $state(null);
	/** Default master template ID for newsletters. */
	let defaultMasterTemplateId: number | null = $state(null);
	/** Available master templates (for the default-template dropdown). */
	let masterTemplates: { value: number | string; name: string; icon?: string }[] = $state([]);

	// ── S3 Storage fields ─────────────────────────────────────

	/** S3-compatible endpoint URL. */
	let s3Endpoint = $state('');
	/** S3 bucket name. */
	let s3Bucket = $state('');
	/** AWS region for the S3 bucket. */
	let s3Region = $state('');
	/** S3 access key ID. */
	let s3AccessKeyId = $state('');
	/** S3 secret access key. */
	let s3SecretAccessKey = $state('');
	/** Optional public base URL for serving S3 files. */
	let s3PublicUrl = $state('');
	// ── Google OAuth fields ──────────────────────────────────────

	/** Google OAuth 2.0 client ID for Sign-In button. */
	let googleClientId = $state('');
	/** Google OAuth 2.0 client secret for server-side token verification. */
	let googleClientSecret = $state('');
	/** Tracks the masked value returned by the server so unchanged secrets are not re-sent. */
	let _originalMaskedSecret = $state('');

	// ── Sign in with Apple fields ────────────────────────────────

	/** Apple Services ID acting as the OAuth client ID. */
	let appleClientId = $state('');
	/** Apple Developer Team ID that issues the client secret. */
	let appleTeamId = $state('');
	/** Identifier of the Apple `.p8` signing key. */
	let appleKeyId = $state('');
	/** PEM contents of the Apple `.p8` signing key. */
	let applePrivateKey = $state('');
	/** Return URL registered with Apple for this Services ID. */
	let appleRedirectURI = $state('');
	/** Tracks the masked signing key returned by the server so it is not re-sent unchanged. */
	let _originalMaskedAppleKey = $state('');

	// ── Sign in with Microsoft fields ────────────────────────────

	/** Entra ID application (client) ID. */
	let microsoftClientId = $state('');
	/** Client secret issued for the Entra app registration. */
	let microsoftClientSecret = $state('');
	/** Directory (tenant) ID, or blank for the multi-tenant `common` authority. */
	let microsoftTenantId = $state('');
	/** Redirect URI registered on the Entra app. */
	let microsoftRedirectURI = $state('');
	/** Tracks the masked client secret returned by the server so it is not re-sent unchanged. */
	let _originalMaskedMicrosoftSecret = $state('');

	// ── Upload state ──────────────────────────────────────────

	/** API base URL for constructing image preview URLs. */
	const apiBase = site.backend.baseURL;

	/**
	 * Load the current settings from the server and populate the form.
	 */
	onMount(async () => {
		try {
			const [s, templates] = await Promise.all([getSettings(), listEmailTemplates().catch(() => [])]);
			settings = s;
			if (settings) populateForm(settings);
			else error = t2('No settings found. Save defaults to create the settings row.');
			/* Build master template options for dropdown. */
			masterTemplates = [
				{ value: '', name: '— Use first master template —', icon: 'fa-solid fa-layer-group' },
				...templates
					.filter((t) => t.templateType === 'master')
					.map((t) => ({ value: t.id, name: t.templateName, icon: 'fa-solid fa-file-code' }))
			];
		} catch (err) {
			error = err instanceof Error ? err.message : t2('Failed to load settings');
		} finally {
			loading = false;
		}
	});

	/**
	 * Populate form fields from a Settings object.
	 * @param s - The settings object to read from.
	 */
	function populateForm(s: Settings): void {
		website = s.website || '';
		websiteName = s.websiteName || '';
		termsOfServiceURL = s.termsOfServiceURL || '';
		loginHeading = s.loginHeading || '';
		loginSubheading = s.loginSubheading || '';
		migrationContactEmail = s.migrationContactEmail || '';
		isActive = s.isActive;
		sidebarLinks = s.customSidebarLinks ? s.customSidebarLinks.map((l) => ({ ...l })) : [];
		defaultMasterTemplateId = s.defaultMasterTemplateId;
		s3Endpoint = s.s3Endpoint || '';
		s3Bucket = s.s3Bucket || '';
		s3Region = s.s3Region || '';
		s3AccessKeyId = s.s3AccessKeyId || '';
		s3SecretAccessKey = s.s3SecretAccessKey || '';
		s3PublicUrl = s.s3PublicUrl || '';
		googleClientId = s.googleClientId || '';
		googleClientSecret = s.googleClientSecret || '';
		/** Remember the masked value from the server so we know if the user changed it. */
		_originalMaskedSecret = googleClientSecret;
		appleClientId = s.appleClientId || '';
		appleTeamId = s.appleTeamId || '';
		appleKeyId = s.appleKeyId || '';
		applePrivateKey = s.applePrivateKey || '';
		appleRedirectURI = s.appleRedirectURI || '';
		/** Same for the Apple signing key, which the server also returns masked. */
		_originalMaskedAppleKey = applePrivateKey;
		microsoftClientId = s.microsoftClientId || '';
		microsoftClientSecret = s.microsoftClientSecret || '';
		microsoftTenantId = s.microsoftTenantId || '';
		microsoftRedirectURI = s.microsoftRedirectURI || '';
		/** And for the Microsoft client secret. */
		_originalMaskedMicrosoftSecret = microsoftClientSecret;
	}

	/**
	 * Save the current form values to the server.
	 */
	async function handleSave(): Promise<void> {
		saving = true;
		error = '';

		/** Send the updated settings to the server and refresh the form. */
		try {
			settings = await updateSettings({
				website: website || null,
				websiteName: websiteName || null,
				termsOfServiceURL: termsOfServiceURL || null,
				loginHeading: loginHeading || null,
				loginSubheading: loginSubheading || null,
				migrationContactEmail: migrationContactEmail || null,
				isActive,
				customSidebarLinks: sidebarLinks.length > 0 ? sidebarLinks : null,
				defaultMasterTemplateId: defaultMasterTemplateId || null,
				s3Endpoint: s3Endpoint || null,
				s3Bucket: s3Bucket || null,
				s3Region: s3Region || null,
				s3AccessKeyId: s3AccessKeyId || null,
				s3SecretAccessKey: s3SecretAccessKey || null,
				s3PublicUrl: s3PublicUrl || null,
				googleClientId: googleClientId || null,
				appleClientId: appleClientId || null,
				appleTeamId: appleTeamId || null,
				appleKeyId: appleKeyId || null,
				appleRedirectURI: appleRedirectURI || null,
				/* Only send the secret if the user typed a new value; skip the masked placeholder. */
				...(googleClientSecret && googleClientSecret !== _originalMaskedSecret ? { googleClientSecret } : {}),
				microsoftClientId: microsoftClientId || null,
				microsoftTenantId: microsoftTenantId || null,
				microsoftRedirectURI: microsoftRedirectURI || null,
				/* Same for the Apple signing key. */
				...(applePrivateKey && applePrivateKey !== _originalMaskedAppleKey ? { applePrivateKey } : {}),
				/* And the Microsoft client secret. */
				...(microsoftClientSecret && microsoftClientSecret !== _originalMaskedMicrosoftSecret
					? { microsoftClientSecret }
					: {})
			});
			populateForm(settings);
			toastSuccess(t2('Settings saved successfully.'));
		} catch (err) {
			toastError(err instanceof Error ? err.message : t2('Failed to save settings'));
		} finally {
			saving = false;
		}
	}

	// ── Sidebar link icon upload handlers ─────────────────────

	/**
	 * Handle icon file selection and upload for a sidebar link.
	 * @param index - Zero-based index of the sidebar link.
	 * @param event - File input change event.
	 */
	async function handleLinkIconUpload(index: number, event: Event): Promise<void> {
		const input = event.target as HTMLInputElement;
		const file = input.files?.[0];
		/** Bail out if no file was selected. */
		if (!file) return;

		uploadingLinkIcon = index;
		/** Upload the icon and refresh sidebar link state from the response. */
		try {
			settings = await uploadSidebarLinkIcon(index, file);
			populateForm(settings);
			toastSuccess(t2('Sidebar link icon uploaded.'));
		} catch (e) {
			toastError(e instanceof Error ? e.message : t2('Failed to upload icon'));
		} finally {
			uploadingLinkIcon = null;
			input.value = '';
		}
	}

	/**
	 * Remove the icon from a sidebar link.
	 * @param index - Zero-based index of the sidebar link.
	 */
	async function handleLinkIconDelete(index: number): Promise<void> {
		try {
			settings = await deleteSidebarLinkIcon(index);
			populateForm(settings);
			toastSuccess(t2('Sidebar link icon removed.'));
		} catch (e) {
			toastError(e instanceof Error ? e.message : t2('Failed to delete icon'));
		}
	}
</script>

<Heading tag="h3">{$t("General Settings")}</Heading>

{#if loading}
	<div class="flex items-center justify-center py-12">
		<Spinner size="8" />
	</div>
{:else}
	<div class="mt-4 space-y-6">
		{#if error}
			<div class="rounded-lg bg-red-50 p-4 text-red-700 dark:bg-red-900/20 dark:text-red-300">
				<i class="fa-solid fa-exclamation-triangle me-2"></i>{error}
			</div>
		{/if}

		<!-- Public Content -->
		<AbstractCard class="max-w-none">
			<h4 class="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
				<i class="fa-solid fa-globe me-2"></i>{$t("Public Content")}
			</h4>
			<Helper class="mb-3">{$t("Branding text and links shown on the login and registration pages.")}</Helper>

			<div class="grid grid-cols-1 gap-4 md:grid-cols-2">
				<div>
					<Label for="loginHeading" class="mb-2">{$t("Login Heading")}</Label>
					<Input id="loginHeading" bind:value={loginHeading} placeholder="Identity, simplified." />
					<Helper class="mt-1">{$t("Large heading on the auth page branding panel.")}</Helper>
				</div>

				<div>
					<Label for="loginSubheading" class="mb-2">{$t("Login Subheading")}</Label>
					<Input
						id="loginSubheading"
						bind:value={loginSubheading}
						placeholder="Secure authentication, role management, and user administration — all in one place."
					/>
					<Helper class="mt-1">{$t("Paragraph text below the heading.")}</Helper>
				</div>

				<div>
					<Label for="websiteName" class="mb-2">{$t("Website Name")}</Label>
					<Input id="websiteName" bind:value={websiteName} placeholder="Abstract" />
					<Helper class="mt-1">{$t("Display name for the website link on auth pages.")}</Helper>
				</div>

				<div>
					<Label for="website" class="mb-2">{$t("Website URL")}</Label>
					<Input id="website" bind:value={website} placeholder="https://abstract3d.com" />
					<Helper class="mt-1">{$t("Linked from login/register pages and emails.")}</Helper>
				</div>

				<div>
					<Label for="termsOfServiceURL" class="mb-2">{$t("Terms of Service URL")}</Label>
					<Input
						id="termsOfServiceURL"
						bind:value={termsOfServiceURL}
						placeholder="https://abstract3d.com/terms"
					/>
					<Helper class="mt-1">{$t("Linked on login and registration pages.")}</Helper>
				</div>

				<div>
					<Label for="migrationContactEmail" class="mb-2">{$t("Migration Contact Email")}</Label>
					<Input
						id="migrationContactEmail"
						bind:value={migrationContactEmail}
						placeholder="support@example.com"
					/>
					<Helper class="mt-1">{$t("Shown during account migration / data transfer.")}</Helper>
				</div>
			</div>
		</AbstractCard>

		<!-- Custom Sidebar Links -->
		<AbstractCard class="max-w-none">
			<h4 class="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
				<i class="fa-solid fa-link me-2"></i>{$t("Custom Sidebar Links")}
			</h4>
			<Helper class="mb-3"
				>{$t("Add external links that appear in the application sidebar for all users. Upload an icon image (128×128 PNG) and set the order to control placement alongside registered applications.")}</Helper
			>

			{#each sidebarLinks as link, i (i)}
				<div class="mb-3 rounded-lg border border-gray-200 p-4 dark:border-gray-700">
					<div class="mb-3 flex flex-wrap items-end gap-2">
						<div class="min-w-[120px] flex-1">
							<Label class="mb-1">{$t("Label")}</Label>
							<Input bind:value={link.label} placeholder="Documentation" size="sm" />
						</div>
						<div class="min-w-[200px] flex-[2]">
							<Label class="mb-1">{$t("URL")}</Label>
							<Input bind:value={link.url} placeholder="https://..." size="sm" />
						</div>
						<div class="w-24">
							<Label class="mb-1">{$t("Order")}</Label>
							<Input type="number" bind:value={link.order} placeholder="0" size="sm" />
						</div>
						<Button
							size="xs"
							color="red"
							onclick={() => {
								sidebarLinks = sidebarLinks.filter((_, idx) => idx !== i);
							}}
						>
							<i class="fa-solid fa-trash"></i>
						</Button>
					</div>

					<!-- Icon Upload -->
					<div class="flex items-center gap-3">
						{#if link.iconImageName}
							<img
								src="{apiBase}/uploads/branding/{link.iconImageName}"
								alt={$t("Link icon")}
								class="h-10 w-10 rounded border bg-gray-50 object-contain dark:border-gray-600 dark:bg-gray-800"
							/>
							<ConfirmButton
								size="xs"
								color="red"
								outline
								action={$t("remove this icon")}
								onconfirm={() => handleLinkIconDelete(i)}
							>
								<i class="fa-solid fa-trash me-1"></i>{$t("Remove Icon")}
							</ConfirmButton>
						{/if}
						<input
							id="sidebar-link-icon-{i}"
							type="file"
							accept="image/*"
							class="hidden"
							onchange={(e) => handleLinkIconUpload(i, e)}
							disabled={uploadingLinkIcon === i}
						/>
						<Button
							size="xs"
							color="alternative"
							disabled={uploadingLinkIcon === i}
							onclick={() => document.getElementById(`sidebar-link-icon-${i}`)?.click()}
						>
							{#if uploadingLinkIcon === i}
								<Spinner size="4" class="mr-2" />{$t("Uploading…")}
							{:else}
								<i class="fa-solid fa-upload me-1"></i>{link.iconImageName
									? $t('Replace Icon')
									: $t('Upload Icon')}
							{/if}
						</Button>
					</div>
				</div>
			{/each}

			<Button
				size="sm"
				color="alternative"
				onclick={() => {
					sidebarLinks = [
						...sidebarLinks,
						{ label: '', url: '', iconImageName: null, order: sidebarLinks.length }
					];
				}}
			>
				<i class="fa-solid fa-plus me-1"></i>{$t("Add Link")}
			</Button>
		</AbstractCard>

		<!-- Default Email Template -->
		<AbstractCard class="max-w-none">
			<h4 class="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
				<i class="fa-solid fa-envelope me-2"></i>{$t("Default Email Master Template")}
			</h4>
			<Helper class="mb-3">{$t("The master template used by newsletters when no specific template is selected.")}</Helper>

			<div class="max-w-md">
				<AbstractDropdown items={masterTemplates} value={defaultMasterTemplateId ?? ''} onchange={(v) => { defaultMasterTemplateId = v === '' ? null : v as number; }} class="w-full" />
			</div>
		</AbstractCard>

		<!-- S3 Storage Configuration -->
		<AbstractCard class="max-w-none">
			<h4 class="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
				<i class="fa-solid fa-cloud-arrow-up me-2"></i>{$t("S3 Storage")}
			</h4>
			<Helper class="mb-3">
				{$t("Configure an S3-compatible storage backend for file uploads (AWS S3, MinIO, DigitalOcean Spaces, etc.).")}
				{$t("Leave all fields empty to use local disk storage.")}
			</Helper>

			<div class="grid grid-cols-1 gap-4 md:grid-cols-2">
				<div>
					<Label for="s3Endpoint" class="mb-2">{$t("Endpoint URL")}</Label>
					<Input id="s3Endpoint" bind:value={s3Endpoint} placeholder="https://s3.amazonaws.com" />
					<Helper class="mt-1"
						>{$t("S3-compatible endpoint (e.g. https://s3.amazonaws.com or your MinIO URL).")}</Helper
					>
				</div>

				<div>
					<Label for="s3Bucket" class="mb-2">{$t("Bucket Name")}</Label>
					<Input id="s3Bucket" bind:value={s3Bucket} placeholder="my-app-uploads" />
				</div>

				<div>
					<Label for="s3Region" class="mb-2">{$t("Region")}</Label>
					<Input id="s3Region" bind:value={s3Region} placeholder="us-east-1" />
				</div>

				<div>
					<Label for="s3AccessKeyId" class="mb-2">{$t("Access Key ID")}</Label>
					<Input id="s3AccessKeyId" bind:value={s3AccessKeyId} placeholder="AKIAIOSFODNN7EXAMPLE" />
				</div>

				<div>
					<Label for="s3SecretAccessKey" class="mb-2">{$t("Secret Access Key")}</Label>
					<Input
						id="s3SecretAccessKey"
						type="password"
						bind:value={s3SecretAccessKey}
						placeholder="wJalrXUtnFEMI/K7MDENG/bPxRfiCYEXAMPLEKEY"
					/>
				</div>

				<div>
					<Label for="s3PublicUrl" class="mb-2">{$t("Public URL (optional)")}</Label>
					<Input id="s3PublicUrl" bind:value={s3PublicUrl} placeholder="https://cdn.example.com/uploads" />
					<Helper class="mt-1">{$t("Base URL for accessing files publicly. If empty, uses the default path.")}</Helper
					>
				</div>
			</div>
		</AbstractCard>

		<!-- Google OAuth -->
		<AbstractCard class="max-w-none">
			<h4 class="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
				<i class="fa-brands fa-google me-2"></i>{$t("Google Sign-In")}
			</h4>
			<Helper class="mb-3">
				{$t('Configure Google OAuth 2.0 to show the "Sign in with Google" button on the login page.')}
				{$t("Create credentials at")} <a
					href="https://console.cloud.google.com/apis/credentials"
					target="_blank"
					rel="noopener noreferrer"
					class="underline">Google Cloud Console</a
				>.
			</Helper>

			<div class="grid grid-cols-1 gap-4 md:grid-cols-2">
				<div>
					<Label for="googleClientId" class="mb-2">{$t("Google Client ID")}</Label>
					<Input
						id="googleClientId"
						bind:value={googleClientId}
						placeholder="123456789.apps.googleusercontent.com"
					/>
					<Helper class="mt-1">{$t("Leave empty to disable Google Sign-In.")}</Helper>
				</div>

				<div>
					<Label for="googleClientSecret" class="mb-2">
						{$t("Google Client Secret")}
						{#if _originalMaskedSecret}
							<i class="fa-solid fa-circle-check ms-1 text-green-500" title={$t("Secret is configured")}></i>
						{/if}
					</Label>
					<Input
						id="googleClientSecret"
						type="password"
						bind:value={googleClientSecret}
						placeholder="XOYSPX-..."
					/>
					<Helper class="mt-1">{$t("Required for server-side token verification.")}</Helper>
				</div>
			</div>
		</AbstractCard>

		<!-- Sign in with Apple -->
		<AbstractCard class="max-w-none">
			<h4 class="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
				<i class="fa-brands fa-apple me-2"></i>{$t("Sign in with Apple")}
			</h4>
			<Helper class="mb-3">
				{$t('Configure a Services ID and signing key to show the "Sign in with Apple" button on the login page.')}
				{$t("Create credentials at")} <a
					href="https://developer.apple.com/account/resources/identifiers/list/serviceId"
					target="_blank"
					rel="noopener noreferrer"
					class="underline">Apple Developer</a
				>.
			</Helper>

			<div class="grid grid-cols-1 gap-4 md:grid-cols-2">
				<div>
					<Label for="appleClientId" class="mb-2">{$t("Apple Services ID")}</Label>
					<Input id="appleClientId" bind:value={appleClientId} placeholder="com.example.id.web" />
					<Helper class="mt-1">{$t("Leave empty to disable Sign in with Apple.")}</Helper>
				</div>

				<div>
					<Label for="appleRedirectURI" class="mb-2">{$t("Return URL")}</Label>
					<Input id="appleRedirectURI" bind:value={appleRedirectURI} placeholder="https://id.example.com/login" />
					<Helper class="mt-1">{$t("Must match a Return URL registered on the Services ID exactly.")}</Helper>
				</div>

				<div>
					<Label for="appleTeamId" class="mb-2">{$t("Apple Team ID")}</Label>
					<Input id="appleTeamId" bind:value={appleTeamId} placeholder="ABCDE12345" />
					<Helper class="mt-1">{$t("Found in the top-right of the Apple Developer portal.")}</Helper>
				</div>

				<div>
					<Label for="appleKeyId" class="mb-2">{$t("Apple Key ID")}</Label>
					<Input id="appleKeyId" bind:value={appleKeyId} placeholder="XYZ9876543" />
					<Helper class="mt-1">{$t("Identifier of the .p8 key enabled for Sign in with Apple.")}</Helper>
				</div>

				<div class="md:col-span-2">
					<Label for="applePrivateKey" class="mb-2">
						{$t("Apple Private Key (.p8)")}
						{#if _originalMaskedAppleKey}
							<i class="fa-solid fa-circle-check ms-1 text-green-500" title={$t("Secret is configured")}></i>
						{/if}
					</Label>
					<Textarea
						id="applePrivateKey"
						rows={4}
						bind:value={applePrivateKey}
						placeholder="-----BEGIN PRIVATE KEY-----"
					/>
					<Helper class="mt-1">{$t("Paste the full contents of the .p8 file downloaded from Apple.")}</Helper>
				</div>
			</div>
		</AbstractCard>

		<!-- Sign in with Microsoft -->
		<AbstractCard class="max-w-none">
			<h4 class="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
				<i class="fa-brands fa-microsoft me-2"></i>{$t("Sign in with Microsoft")}
			</h4>
			<Helper class="mb-3">
				{$t('Configure an Entra ID app registration to show the "Sign in with Microsoft" button on the login page.')}
				{$t("Create credentials at")} <a
					href="https://entra.microsoft.com/#view/Microsoft_AAD_RegisteredApps/ApplicationsListBlade"
					target="_blank"
					rel="noopener noreferrer"
					class="underline">Microsoft Entra admin center</a
				>.
			</Helper>

			<div class="grid grid-cols-1 gap-4 md:grid-cols-2">
				<div>
					<Label for="microsoftClientId" class="mb-2">{$t("Application (client) ID")}</Label>
					<Input id="microsoftClientId" bind:value={microsoftClientId} placeholder="00000000-0000-0000-0000-000000000000" />
					<Helper class="mt-1">{$t("Leave empty to disable Sign in with Microsoft.")}</Helper>
				</div>

				<div>
					<Label for="microsoftClientSecret" class="mb-2">
						{$t("Client Secret")}
						{#if _originalMaskedMicrosoftSecret}
							<i class="fa-solid fa-circle-check ms-1 text-green-500" title={$t("Secret is configured")}></i>
						{/if}
					</Label>
					<Input id="microsoftClientSecret" type="password" bind:value={microsoftClientSecret} placeholder="~8Q..." />
					<Helper class="mt-1">{$t("The secret value, not its ID. Secrets expire — note the expiry date.")}</Helper>
				</div>

				<div>
					<Label for="microsoftTenantId" class="mb-2">{$t("Directory (tenant) ID")}</Label>
					<Input id="microsoftTenantId" bind:value={microsoftTenantId} placeholder="common" />
					<Helper class="mt-1"
						>{$t("Leave empty to accept any Microsoft account. Set a directory ID to restrict sign-in to one organisation.")}</Helper
					>
				</div>

				<div>
					<Label for="microsoftRedirectURI" class="mb-2">{$t("Redirect URI")}</Label>
					<Input
						id="microsoftRedirectURI"
						bind:value={microsoftRedirectURI}
						placeholder="https://id.example.com/auth/microsoft/callback"
					/>
					<Helper class="mt-1"
						>{$t("Register this exact URL as a Web redirect URI on the app. It must end in /auth/microsoft/callback.")}</Helper
					>
				</div>
			</div>
		</AbstractCard>

		<!-- System -->
		<AbstractCard class="max-w-none">
			<h4 class="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
				<i class="fa-solid fa-power-off me-2"></i>{$t("System")}
			</h4>

			<div class="flex items-center gap-3">
				<Toggle bind:checked={isActive} />
				<div>
					<p class="font-medium text-gray-900 dark:text-white">{$t("Service Active")}</p>
					<p class="text-sm text-gray-500 dark:text-gray-400">
						{$t("When disabled, the identity service rejects new registrations.")}
					</p>
				</div>
			</div>
		</AbstractCard>

		<!-- Save -->
		<div class="flex justify-end">
			<Button onclick={handleSave} disabled={saving}>
				{#if saving}
					<i class="fa-solid fa-spinner fa-spin me-2"></i>{$t("Saving...")}
				{:else}
					<i class="fa-solid fa-save me-2"></i>{$t("Save Settings")}
				{/if}
			</Button>
		</div>
	</div>
{/if}
